import { DataResult, process, State } from '@progress/kendo-data-query';
import { Button } from '@progress/kendo-react-buttons';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Grid, GridCellProps, GridColumn } from '@progress/kendo-react-grid';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { ApiEnums } from '../../Interfaces/ApiEnums';
import { ApplicationState } from '../../store';
import { getProductPackagesHealthInfo } from '../../store/ProductsManagement/ProductsActions';
import { getLocalDateTime } from '../../utils/utils';
import { LoadingPanel } from '../LoadingPanel';
import { GetString } from '../StringResourceManager';

interface Props {
  nodeId: number | null;
  machineName: string;
  visible: boolean;
  onClose: () => void;
}

const ProductPackagesHealthDialog: React.FC<Props> = ({
  nodeId,
  machineName,
  visible,
  onClose,
}): React.ReactElement | null => {
  const dispatch: Dispatch<any> = useDispatch();

  const isFetchingProductHealthInfo = useSelector(
    (state: ApplicationState) => state.productsReducer.isFetching,
  );

  const packageHealthInfo = useSelector(
    (state: ApplicationState) =>
      state.productsReducer.productPackagesHealthInfo,
  );

  const [dataState, setDataState] = React.useState<State>({
    skip: 0,
    take: 25,
  });

  const [resultState, setResultState] = React.useState<DataResult>(
    process(packageHealthInfo ?? [], dataState),
  );

  const PRODUCT_PACKAGES = GetString('PRODUCT_PACKAGES');
  const COLUMN_TITLE_PACKAGE_NAME = GetString('COLUMN_TITLE_PACKAGE_NAME');
  const COLUMN_TITLE_CURRENT_VERSION = GetString(
    'COLUMN_TITLE_CURRENT_VERSION',
  );
  const STATUS = GetString('STATUS');
  const COLUMN_TITLE_FAILURE_MESSAGE = GetString(
    'COLUMN_TITLE_FAILURE_MESSAGE',
  );
  const COLUMN_TITLE_LANE_UPDATED = GetString('COLUMN_TITLE_LANE_UPDATED');
  const CLOSE_STR = GetString('CLOSE_STR');

  useEffect(() => {
    setResultState(process(packageHealthInfo ?? [], dataState));
  }, [packageHealthInfo, dataState]);

  useEffect(() => {
    if (visible && nodeId && nodeId > 0) {
      getProductPackagesHealthInfo(dispatch, nodeId);
    }
  }, [visible, nodeId]);

  if (!visible) {
    return null;
  }

  const dataStateChange = (event: any): void => {
    setDataState(event.dataState);

    setResultState(process(packageHealthInfo ?? [], event.dataState));
  };

  const DateTimeCell = (props: GridCellProps): JSX.Element => {
    const value = props.dataItem?.[props.field ?? ''];

    return (
      <td>
        {value && value !== '0001-01-01T00:00:00'
          ? getLocalDateTime(value)
          : ''}
      </td>
    );
  };

  const StatusCell = (props: GridCellProps): JSX.Element => {
    const status = props.dataItem?.Status as ApiEnums.ProductDeploymentStatus;

    const backgroundColor =
      status === ApiEnums.ProductDeploymentStatus.Healthy
        ? 'rgb(55, 180, 0, 0.32)'
        : status === ApiEnums.ProductDeploymentStatus.Error
          ? 'rgb(243, 23, 0, 0.32)'
          : undefined;

    return (
      <td style={{ backgroundColor }}>
        {ApiEnums.ProductDeploymentStatus[status]}
      </td>
    );
  };

  return (
    <Dialog
      width={1100}
      height={500}
      title={`${PRODUCT_PACKAGES} - ${machineName}`}
      onClose={onClose}
    >
      {isFetchingProductHealthInfo && LoadingPanel}
      <Grid
        data={resultState}
        {...dataState}
        sortable={true}
        resizable={true}
        filterable={true}
        pageable={{
          buttonCount: 4,
          pageSizes: [25, 50, 100],
        }}
        onDataStateChange={dataStateChange}
      >
        <GridColumn
          field="PackageName"
          title={COLUMN_TITLE_PACKAGE_NAME}
          width="200px"
        />
        <GridColumn
          field="PackageVersion"
          title={COLUMN_TITLE_CURRENT_VERSION}
          width="170px"
        />
        <GridColumn
          field="Status"
          title={STATUS}
          width="130px"
          cells={{ data: StatusCell }}
        />
        <GridColumn
          field="StatusMessage"
          title={COLUMN_TITLE_FAILURE_MESSAGE}
          width="300px"
        />
        <GridColumn
          field="LastHeartbeat"
          title={COLUMN_TITLE_LANE_UPDATED}
          width="200px"
          cells={{ data: DateTimeCell }}
        />
      </Grid>

      <DialogActionsBar layout="center">
        <Button themeColor={'primary'} type="button" onClick={onClose}>
          {CLOSE_STR}
        </Button>
      </DialogActionsBar>
    </Dialog>
  );
};

export default ProductPackagesHealthDialog;
